import React, { useState, useEffect } from 'react';
import { TrendingUp, CheckCircle, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RateComparison = () => {
  const [rates, setRates] = useState({
    saverr: { rate: 3.75, lastUpdated: new Date() },
    marcus: { rate: 3.65, lastUpdated: new Date() },
    wealthfront: { rate: 3.50, lastUpdated: new Date() },
    betterment: { rate: 3.45, lastUpdated: new Date() }
  });

  const [isUpdating, setIsUpdating] = useState(false);

  // Fetch Saverr's rate from backend
  useEffect(() => {
    const fetchSaverrRate = async () => {
      try {
        const response = await axios.get(`${API}/settings/apy`);
        setRates(prev => ({
          ...prev,
          saverr: { rate: response.data.apy_rate, lastUpdated: new Date() }
        }));
      } catch (error) {
        console.error('Error fetching Saverr rate:', error);
      }
    };
    fetchSaverrRate();
  }, []);

  // Simulate live rate updates every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsUpdating(true);
      
      setTimeout(() => {
        setRates(prev => ({
          ...prev,
          marcus: { 
            rate: parseFloat((3.60 + Math.random() * 0.10).toFixed(2)), 
            lastUpdated: new Date() 
          },
          wealthfront: { 
            rate: parseFloat((3.45 + Math.random() * 0.10).toFixed(2)), 
            lastUpdated: new Date() 
          },
          betterment: { 
            rate: parseFloat((3.40 + Math.random() * 0.10).toFixed(2)), 
            lastUpdated: new Date() 
          }
        }));
        setIsUpdating(false);
      }, 1000);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const formatTime = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    return `${Math.floor(diffInSeconds / 3600)}h ago`;
  };

  const providers = [
    {
      name: 'Saverr',
      key: 'saverr',
      logo: <TrendingUp className="w-5 h-5 text-blue-600" strokeWidth={2.5} />,
      highlight: true,
      description: 'Automated optimization'
    },
    {
      name: 'Marcus by Goldman Sachs',
      key: 'marcus',
      logo: <div className="w-5 h-5 bg-slate-700 rounded-full flex items-center justify-center text-white text-xs font-bold">M</div>,
      highlight: false,
      description: 'High-yield savings'
    },
    {
      name: 'Wealthfront',
      key: 'wealthfront',
      logo: <div className="w-5 h-5 bg-emerald-600 rounded-full flex items-center justify-center text-white text-xs font-bold">W</div>,
      highlight: false,
      description: 'Cash account'
    },
    {
      name: 'Betterment',
      key: 'betterment',
      logo: <div className="w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold">B</div>,
      highlight: false,
      description: 'Cash management'
    }
  ];

  const highestRate = Math.max(...Object.values(rates).map(r => r.rate));

  return (
    <section className="py-16 bg-white border-y border-slate-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 border border-blue-100 mb-4">
            <span className={`relative flex h-2 w-2 ${isUpdating ? 'animate-pulse' : ''}`}>
              <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
            </span>
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Live Rate Monitor</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">
            Real-Time Rate Comparison
          </h2>
          <p className="text-lg text-slate-600">
            See how Saverr stacks up against leading cash management accounts
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-700">Provider</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-700">APY</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-700">Account Type</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-700">Last Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {providers.map((provider) => {
                  const rateData = rates[provider.key];
                  const isHighest = rateData.rate === highestRate;
                  
                  return (
                    <tr
                      key={provider.key}
                      className={`transition-colors ${
                        provider.highlight
                          ? 'bg-blue-50 hover:bg-blue-100'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          {provider.logo}
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-900">{provider.name}</span>
                              {provider.highlight && (
                                <span className="bg-blue-600 text-white px-2 py-0.5 rounded text-xs font-bold">
                                  YOU
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-center gap-2">
                          <span className={`text-3xl font-bold ${
                            provider.highlight ? 'text-blue-600' : 'text-slate-900'
                          }`}>
                            {rateData.rate.toFixed(2)}%
                          </span>
                          {isHighest && (
                            <CheckCircle className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-5 text-center">
                        <span className="text-sm text-slate-600">{provider.description}</span>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                          <Clock className="w-4 h-4" />
                          <span>{formatTime(rateData.lastUpdated)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="bg-slate-50 px-6 py-4 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-600">
                All accounts are FDIC insured up to $250,000
              </p>
              <p className="text-xs text-slate-500">
                Rates update automatically every 30 seconds
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
